import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { generatePDF, generatePDFPreview } from '../utils/pdfGenerator';
import { generateUniqueSKU } from '../utils/skuGenerator';
import { squareAPI } from '../services/squareAPI';
import BrandSelector from './BrandSelector';
import CategorySelector from './CategorySelector';
import ITEM_TYPE_PRESETS from '../data/itemTypePresets.json';
import { formatMoney } from '../utils/formatters';

const ItemCreation = ({ onItemCreated, brands = [], onCreateBrand, categories = [], onCreateCategory }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0); // Start at 0 for item type selection
  const [itemData, setItemData] = useState({
    name: '',
    brand: '',
    category: '',
    defaultPrice: '',
    cost: '',
    itemType: '',
    colors: []
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // New state for matrix-based selection
  const [colorList, setColorList] = useState([]);
  const [sizeList, setSizeList] = useState([]);
  const [matrixSelections, setMatrixSelections] = useState({});
  const [subStep, setSubStep] = useState('a'); // 'a' for input, 'b' for matrix

  // Print format state
  const [printFormat, setPrintFormat] = useState('swing-tags'); // 'swing-tags' or 'avery-labels'
  const [pdfPreview, setPdfPreview] = useState(null);
  const [createdItem, setCreatedItem] = useState(null);

  // Avery label starting position
  const [startRow, setStartRow] = useState(1);
  const [startCol, setStartCol] = useState(1);

  // Step 0: Item Type Selection
  const handleStep0 = (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!itemData.itemType) {
      newErrors.itemType = 'Please select an item type';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Set default print format based on item type
    if (itemData.itemType === 'shoes') {
      setPrintFormat('avery-labels');
    } else {
      setPrintFormat('swing-tags');
    }

    setErrors({});
    setCurrentStep(1);
  };

  // Step 1: Basic Information
  const handleStep1 = (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!itemData.name.trim()) {
      newErrors.name = 'Item name is required';
    }
    if (!itemData.defaultPrice || itemData.defaultPrice <= 0) {
      newErrors.defaultPrice = 'Valid price is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    // Prefill colors/sizes from item type preset if lists are empty
    if (itemData.itemType && colorList.length === 0 && sizeList.length === 0) {
      const preset = ITEM_TYPE_PRESETS[itemData.itemType];
      if (preset) {
        //setColorList([...preset.colors]); // Copy arrays to allow modification
        //setSizeList([...preset.sizes]);
      }
    }

    setCurrentStep(2);
    setSubStep('a');
  };

  // Step 2A: Input Colors and Sizes
  const toggleColorInList = (colorName) => {
    if (colorList.includes(colorName)) {
      // Remove color
      const newColors = colorList.filter(c => c !== colorName);
      setColorList(newColors);
      // Remove matrix selections related to this color
      const newSelections = { ...matrixSelections };
      Object.keys(newSelections).forEach(key => {
        if (key.startsWith(`${colorName}-`)) {
          delete newSelections[key];
        }
      });
      setMatrixSelections(newSelections);
    } else {
      // Add color
      setColorList([...colorList, colorName]);
    }
  };

  const toggleSizeInList = (sizeName) => {
    if (sizeList.includes(sizeName)) {
      // Remove size
      const newSizes = sizeList.filter(s => s !== sizeName);
      setSizeList(newSizes);
      // Remove matrix selections related to this size
      const newSelections = { ...matrixSelections };
      Object.keys(newSelections).forEach(key => {
        if (key.endsWith(`-${sizeName}`)) {
          delete newSelections[key];
        }
      });
      setMatrixSelections(newSelections);
    } else {
      // Add size
      setSizeList([...sizeList, sizeName]);
    }
  };

  const addCustomColor = () => {
    const customColors = prompt('Enter color(s) - separate multiple colors with commas (e.g., Red, Green, Blue):');
    if (customColors && customColors.trim()) {
      // Split by comma and process each color
      const colorsToAdd = customColors
        .split(',')
        .map(c => c.trim())
        .filter(c => c && !colorList.includes(c)); // Filter out empty strings and duplicates

      if (colorsToAdd.length > 0) {
        setColorList([...colorList, ...colorsToAdd]);
      }
    }
  };

  const addCustomSize = () => {
    const customSizes = prompt('Enter size(s) - separate multiple sizes with commas (e.g., S, M, L, XL):');
    if (customSizes && customSizes.trim()) {
      // Split by comma and process each size
      const sizesToAdd = customSizes
        .split(',')
        .map(s => s.trim())
        .filter(s => s && !sizeList.includes(s)); // Filter out empty strings and duplicates

      if (sizesToAdd.length > 0) {
        setSizeList([...sizeList, ...sizesToAdd]);
      }
    }
  };

  const removeColorFromList = (colorName) => {
    const newColors = colorList.filter(c => c !== colorName);
    setColorList(newColors);
    // Remove matrix selections related to this color
    const newSelections = { ...matrixSelections };
    Object.keys(newSelections).forEach(key => {
      if (key.startsWith(`${colorName}-`)) {
        delete newSelections[key];
      }
    });
    setMatrixSelections(newSelections);
  };

  const removeSizeFromList = (sizeName) => {
    const newSizes = sizeList.filter(s => s !== sizeName);
    setSizeList(newSizes);
    // Remove matrix selections related to this size
    const newSelections = { ...matrixSelections };
    Object.keys(newSelections).forEach(key => {
      if (key.endsWith(`-${sizeName}`)) {
        delete newSelections[key];
      }
    });
    setMatrixSelections(newSelections);
  };

  const handleStep2A = (e) => {
    e.preventDefault();
    const newErrors = {};

    if (colorList.length === 0) {
      newErrors.colors = 'At least one color is required';
    }
    if (sizeList.length === 0) {
      newErrors.sizes = 'At least one size is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setSubStep('b');
  };

  // Step 2B: Matrix Selection
  const toggleMatrixCell = (color, size) => {
    const key = `${color}-${size}`;
    const newSelections = { ...matrixSelections };

    if (newSelections[key]) {
      delete newSelections[key];
    } else {
      newSelections[key] = {
        quantity: 1,
        price: itemData.defaultPrice
      };
    }

    setMatrixSelections(newSelections);
  };

  const updateMatrixCell = (color, size, field, value) => {
    const key = `${color}-${size}`;
    setMatrixSelections({
      ...matrixSelections,
      [key]: {
        ...matrixSelections[key],
        [field]: value
      }
    });
  };

  const selectAllMatrix = () => {
    const newSelections = { ...matrixSelections };
    colorList.forEach(color => {
      sizeList.forEach(size => {
        const key = `${color}-${size}`;
        if (!newSelections[key]) {
          newSelections[key] = {
            quantity: 1,
            price: itemData.defaultPrice
          };
        }
      });
    });
    setMatrixSelections(newSelections);
  };

  const clearAllMatrix = () => {
    setMatrixSelections({});
  };

  const handleStep2 = (e) => {
    e.preventDefault();
    const newErrors = {};

    if (Object.keys(matrixSelections).length === 0) {
      newErrors.matrix = 'At least one color-size combination must be selected';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Convert matrix selections to colors array format for backward compatibility
    const colorsMap = {};
    Object.entries(matrixSelections).forEach(([key, data]) => {
      const [color, size] = key.split('-');
      if (!colorsMap[color]) {
        colorsMap[color] = { name: color, sizes: [] };
      }
      colorsMap[color].sizes.push({
        name: size,
        quantity: parseInt(data.quantity) || 1,
        price: parseFloat(data.price) || parseFloat(itemData.defaultPrice)
      });
    });

    setItemData({
      ...itemData,
      colors: Object.values(colorsMap)
    });

    setErrors({});
    setCurrentStep(3);
  };

  // Step 3: Confirmation
  const getTotalVariations = () => {
    return itemData.colors.reduce((total, color) => {
      return total + color.sizes.reduce((sum, size) => sum + parseInt(size.quantity), 0);
    }, 0);
  };

  const getTotalValue = () => {
    return itemData.colors.reduce((total, color) => {
      return total + color.sizes.reduce((sum, size) => {
        return sum + (parseFloat(size.price) * parseInt(size.quantity));
      }, 0);
    }, 0);
  };

  // Step 3: Save and show preview
  const handleSubmit = async () => {
    setSaving(true);
    try {
      // Generate unique SKU with verification
      const sku = await generateUniqueSKU((sku) => squareAPI.checkSKUExists(sku));

      // Prepare variations for Square API
      const variations = [];
      itemData.colors.forEach(color => {
        color.sizes.forEach(size => {
          variations.push({
            color: color.name,
            size: size.name,
            quantity: parseInt(size.quantity),
            price: parseFloat(size.price),
            sku: `${sku}-${color.name.substring(0, 3).toUpperCase()}-${size.name.toUpperCase()}`
          });
        });
      });

      const item = {
        name: itemData.name,
        brand: itemData.brand,
        category: itemData.category,
        defaultPrice: parseFloat(itemData.defaultPrice),
        cost: itemData.cost ? parseFloat(itemData.cost) : null,
        sku: sku,
        variations: variations,
        createdAt: new Date().toISOString()
      };

      // Save to Square API
      const savedItem = await onItemCreated(item);
      setCreatedItem(savedItem);

      // Generate PDF preview
      const preview = generatePDFPreview(savedItem, printFormat);
      setPdfPreview(preview);

      // Move to preview step (Step 4)
      setCurrentStep(4);
    } catch (error) {
      console.error('Error creating item:', error);
      alert('Error creating item. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Step 4: Download PDF
  const handleDownloadPDF = async () => {
    try {
      const options = printFormat === 'avery-labels' ? { startRow, startCol } : {};
      await generatePDF(createdItem, printFormat, options);
      alert('PDF has been downloaded successfully!');
      navigate('/');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const handleSkipPrint = () => {
    navigate('/');
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {[0, 1, 2, 3, 4].map((step) => (
            <React.Fragment key={step}>
              <div className="flex items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                    currentStep >= step
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {step + 1}
                </div>
                <div className="ml-3">
                  <p className={`text-sm font-medium ${currentStep >= step ? 'text-blue-600' : 'text-gray-500'}`}>
                    {step === 0 && 'Item Type'}
                    {step === 1 && 'Basic Info'}
                    {step === 2 && 'Colors & Sizes'}
                    {step === 3 && 'Confirmation'}
                    {step === 4 && 'Print Preview'}
                  </p>
                </div>
              </div>
              {step < 4 && (
                <div
                  className={`flex-1 h-1 mx-4 ${
                    currentStep > step ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-lg shadow-md p-6">
        {/* Step 0: Item Type Selection */}
        {currentStep === 0 && (
          <form onSubmit={handleStep0}>
            <h2 className="text-2xl font-bold mb-4">Select Item Type</h2>
            <p className="text-gray-600 mb-6">Choose the type of item to get suggested colors and sizes.</p>

            {errors.itemType && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{errors.itemType}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {Object.keys(ITEM_TYPE_PRESETS).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setItemData({ ...itemData, itemType: type })}
                  onDoubleClick={() => {
                    setItemData({ ...itemData, itemType: type });
                    // Set default print format based on item type
                    if (type === 'shoes') {
                      setPrintFormat('avery-labels');
                    } else {
                      setPrintFormat('swing-tags');
                    }
                    setErrors({});
                    setCurrentStep(1);
                  }}
                  className={`p-6 rounded-lg border-2 text-center transition-all cursor-pointer ${
                    itemData.itemType === type
                      ? 'bg-blue-50 border-blue-600 shadow-lg'
                      : 'bg-white border-gray-300 hover:border-blue-400 hover:shadow-md'
                  }`}
                >
                  <div className="text-4xl mb-3">
                    {type === 'clothing' && '👕'}
                    {type === 'shoes' && '👟'}
                    {type === 'accessory' && '👜'}
                  </div>
                  <h3 className={`text-lg font-semibold capitalize ${
                    itemData.itemType === type ? 'text-blue-700' : 'text-gray-800'
                  }`}>
                    {type}
                  </h3>
                  <div className="mt-3 text-xs text-gray-500">
                    {type === 'clothing' && 'Colors & Sizes preset'}
                    {type === 'shoes' && 'Colors & Sizes preset'}
                    {type === 'accessory' && 'Colors preset only'}
                  </div>
                  <div className="mt-2 text-xs text-gray-400 italic">
                    Double-click to continue
                  </div>
                </button>
              ))}
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Next
              </button>
            </div>
          </form>
        )}

        {/* Step 1: Basic Information */}
        {currentStep === 1 && (
          <form onSubmit={handleStep1}>
            <h2 className="text-2xl font-bold mb-6">Basic Information</h2>

            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Item Type:</strong> <span className="capitalize">{itemData.itemType}</span>
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Item Name *
              </label>
              <input
                type="text"
                value={itemData.name}
                onChange={(e) => setItemData({ ...itemData, name: e.target.value })}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., Cotton T-Shirt"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Brand
              </label>
              <BrandSelector
                brands={brands}
                selectedBrand={itemData.brand}
                onChange={(brand) => setItemData({ ...itemData, brand })}
                onCreateBrand={onCreateBrand}
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <CategorySelector
                categories={categories}
                selectedCategory={itemData.category}
                onChange={(category) => setItemData({ ...itemData, category })}
                onCreateCategory={onCreateCategory}
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default Price ($) *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={itemData.defaultPrice}
                onChange={(e) => setItemData({ ...itemData, defaultPrice: e.target.value })}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.defaultPrice ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="29.99"
              />
              {errors.defaultPrice && (
                <p className="mt-1 text-sm text-red-600">{errors.defaultPrice}</p>
              )}
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cost ($)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={itemData.cost}
                onChange={(e) => setItemData({ ...itemData, cost: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="15.00"
              />
              <p className="mt-1 text-xs text-gray-500">Optional - Your wholesale cost for this item</p>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => setCurrentStep(0)}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Back
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Next
              </button>
            </div>
          </form>
        )}

        {/* Step 2: Colors and Sizes */}
        {currentStep === 2 && subStep === 'a' && (
          <form onSubmit={handleStep2A}>
            <h2 className="text-2xl font-bold mb-6">Colors & Sizes</h2>
            <p className="text-sm text-gray-600 mb-6">Select from suggested options or add custom colors and sizes. In the next step, you'll select which combinations apply.</p>

            {errors.colors && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{errors.colors}</p>
              </div>
            )}

            {errors.sizes && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{errors.sizes}</p>
              </div>
            )}

            {/* Colors Section */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-3">Colors</h3>

              {/* Suggested Colors */}
              {itemData.itemType && ITEM_TYPE_PRESETS[itemData.itemType] && (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">Suggested colors for {itemData.itemType}:</p>
                  <div className="flex flex-wrap gap-2">
                    {ITEM_TYPE_PRESETS[itemData.itemType].colors.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => toggleColorInList(color)}
                        className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                          colorList.includes(color)
                            ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                        }`}
                      >
                        {colorList.includes(color) ? '✓ ' : ''}{color}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Selected Colors */}
              {colorList.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Selected colors ({colorList.length}):</p>
                  <div className="flex flex-wrap gap-2">
                    {colorList.map((color) => (
                      <span
                        key={color}
                        className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                      >
                        {color}
                        <button
                          type="button"
                          onClick={() => removeColorFromList(color)}
                          className="ml-2 text-blue-600 hover:text-blue-800 font-bold"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Add Custom Color */}
              <button
                type="button"
                onClick={addCustomColor}
                className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 text-sm"
              >
                + Add Custom Color
              </button>
            </div>

            {/* Sizes Section */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Sizes</h3>

              {/* Suggested Sizes */}
              {itemData.itemType && ITEM_TYPE_PRESETS[itemData.itemType] && ITEM_TYPE_PRESETS[itemData.itemType].sizes.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">Suggested sizes for {itemData.itemType}:</p>
                  <div className="flex flex-wrap gap-2">
                    {ITEM_TYPE_PRESETS[itemData.itemType].sizes.map((size) => (
                      <button
                        key={size}
                        type="button"
                        onClick={() => toggleSizeInList(size)}
                        className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                          sizeList.includes(size)
                            ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                        }`}
                      >
                        {sizeList.includes(size) ? '✓ ' : ''}{size}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Selected Sizes */}
              {sizeList.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Selected sizes ({sizeList.length}):</p>
                  <div className="flex flex-wrap gap-2">
                    {sizeList.map((size) => (
                      <span
                        key={size}
                        className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                      >
                        {size}
                        <button
                          type="button"
                          onClick={() => removeSizeFromList(size)}
                          className="ml-2 text-blue-600 hover:text-blue-800 font-bold"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Add Custom Size */}
              <button
                type="button"
                onClick={addCustomSize}
                className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 text-sm"
              >
                + Add Custom Size
              </button>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => setCurrentStep(1)}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Back
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Next: Select Combinations
              </button>
            </div>
          </form>
        )}

        {/* Step 2B: Matrix Selection */}
        {currentStep === 2 && subStep === 'b' && (
          <form onSubmit={handleStep2}>
            <h2 className="text-2xl font-bold mb-6">Select Color-Size Combinations</h2>
            <p className="text-sm text-gray-600 mb-4">Click on cells to select/deselect combinations. Set quantity and price for each selected variation.</p>

            {errors.matrix && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{errors.matrix}</p>
              </div>
            )}

            {/* Quick Actions */}
            <div className="flex gap-3 mb-4">
              <button
                type="button"
                onClick={selectAllMatrix}
                className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 text-sm"
              >
                Select All
              </button>
              <button
                type="button"
                onClick={clearAllMatrix}
                className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm"
              >
                Clear All
              </button>
            </div>

            {/* Matrix Grid */}
            <div className="overflow-x-auto mb-6">
              <table className="min-w-full border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Color / Size</th>
                    {sizeList.map((size, index) => (
                      <th key={index} className="border border-gray-300 px-4 py-2 text-center font-semibold">
                        {size}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {colorList.map((color, colorIndex) => (
                    <tr key={colorIndex}>
                      <td className="border border-gray-300 px-4 py-2 font-medium bg-gray-50">
                        {color}
                      </td>
                      {sizeList.map((size, sizeIndex) => {
                        const key = `${color}-${size}`;
                        const isSelected = !!matrixSelections[key];
                        return (
                          <td key={sizeIndex} className="border border-gray-300 p-2">
                            <div className="flex flex-col gap-2">
                              <button
                                type="button"
                                onClick={() => toggleMatrixCell(color, size)}
                                className={`w-full px-3 py-2 rounded text-sm font-medium transition-colors ${
                                  isSelected
                                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                                }`}
                              >
                                {isSelected ? '✓ Selected' : 'Select'}
                              </button>
                              {isSelected && (
                                <div className="flex flex-col gap-1">
                                  <input
                                    type="number"
                                    min="1"
                                    value={matrixSelections[key].quantity}
                                    onChange={(e) => updateMatrixCell(color, size, 'quantity', e.target.value)}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                    placeholder="Qty"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={matrixSelections[key].price}
                                    onChange={(e) => updateMatrixCell(color, size, 'price', e.target.value)}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                    placeholder="Price"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => setSubStep('a')}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Back
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Next: Review
              </button>
            </div>
          </form>
        )}

        {/* Step 3: Confirmation */}
        {currentStep === 3 && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Confirmation</h2>

            <div className="space-y-4 mb-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-lg mb-2">{itemData.name}</h3>
                {itemData.brand && <p className="text-gray-600">Brand: {itemData.brand}</p>}
                {itemData.category && <p className="text-gray-600">Category: {itemData.category}</p>}
                <p className="text-gray-600">Default Price: ${itemData.defaultPrice}</p>
                {itemData.cost && <p className="text-gray-600">Cost: ${itemData.cost}</p>}
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-3">Variations</h4>
                {itemData.colors.map((color, ci) => (
                  <div key={ci} className="mb-4 last:mb-0">
                    <p className="font-medium text-sm text-gray-700 mb-2">{color.name}</p>
                    <div className="ml-4 space-y-1">
                      {color.sizes.map((size, si) => (
                        <div key={si} className="flex justify-between text-sm">
                          <span className="text-gray-600">
                            Size {size.name} × {size.quantity}
                          </span>
                          <span className="font-medium">{formatMoney(size.price)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex justify-between mb-2">
                  <span className="text-gray-700">Total Variations:</span>
                  <span className="font-semibold">{getTotalVariations()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Total Inventory Value:</span>
                  <span className="font-semibold text-blue-600">{formatMoney(getTotalValue())}</span>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Next Step:</strong> After creating the item, you'll be able to preview and download your print file (swing tags or labels).
                </p>
              </div>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => {
                  setCurrentStep(2);
                  setSubStep('b'); // Go back to matrix selection, not color/size input
                }}
                disabled={saving}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center"
              >
                {saving ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </>
                ) : (
                  'Create Item & Generate Tags'
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Print Preview */}
        {currentStep === 4 && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Print Preview</h2>
            <p className="text-gray-600 mb-6">Your item has been created successfully! Preview and download your print file.</p>

            {/* Print Format Toggle */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">Print Format</label>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setPrintFormat('swing-tags');
                    if (createdItem) {
                      const preview = generatePDFPreview(createdItem, 'swing-tags');
                      setPdfPreview(preview);
                    }
                  }}
                  className={`flex-1 px-6 py-4 rounded-lg border-2 text-center transition-all ${
                    printFormat === 'swing-tags'
                      ? 'bg-blue-50 border-blue-600 shadow-lg'
                      : 'bg-white border-gray-300 hover:border-blue-400'
                  }`}
                >
                  <div className="text-2xl mb-2">🏷️</div>
                  <h3 className={`font-semibold ${printFormat === 'swing-tags' ? 'text-blue-700' : 'text-gray-800'}`}>
                    Swing Tags
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">2 columns × 7 rows (14 tags/page)</p>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPrintFormat('avery-labels');
                    if (createdItem) {
                      const preview = generatePDFPreview(createdItem, 'avery-labels');
                      setPdfPreview(preview);
                    }
                  }}
                  className={`flex-1 px-6 py-4 rounded-lg border-2 text-center transition-all ${
                    printFormat === 'avery-labels'
                      ? 'bg-blue-50 border-blue-600 shadow-lg'
                      : 'bg-white border-gray-300 hover:border-blue-400'
                  }`}
                >
                  <div className="text-2xl mb-2">📋</div>
                  <h3 className={`font-semibold ${printFormat === 'avery-labels' ? 'text-blue-700' : 'text-gray-800'}`}>
                    Avery 8927 Labels
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">4 columns × 20 rows (80 labels/page)</p>
                </button>
              </div>
            </div>

            {/* Avery Label Starting Position (only show for Avery format) */}
            {printFormat === 'avery-labels' && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Starting Position (for partially used sheets)
                </label>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <label className="block text-xs text-gray-600 mb-1">Start Row (1-20)</label>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={startRow}
                      onChange={(e) => setStartRow(Math.min(20, Math.max(1, parseInt(e.target.value) || 1)))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-gray-600 mb-1">Start Column (1-4)</label>
                    <input
                      type="number"
                      min="1"
                      max="4"
                      value={startCol}
                      onChange={(e) => setStartCol(Math.min(4, Math.max(1, parseInt(e.target.value) || 1)))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setStartRow(1);
                      setStartCol(1);
                    }}
                    className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 mt-5"
                  >
                    Reset
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {startRow === 1 && startCol === 1
                    ? 'Starting from the beginning (full sheet)'
                    : `Labels will start at Row ${startRow}, Column ${startCol} (skipping ${((startRow - 1) * 4) + (startCol - 1)} positions)`
                  }
                </p>
              </div>
            )}

            {/* PDF Preview */}
            {pdfPreview && (
              <div className="mb-6 bg-gray-100 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Preview (First Item)</h3>
                <div className="bg-white rounded shadow-lg overflow-hidden">
                  <iframe
                    src={pdfPreview}
                    className="w-full"
                    style={{ height: '500px' }}
                    title="PDF Preview"
                  />
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between">
              <button
                type="button"
                onClick={handleSkipPrint}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Skip & Go to Inventory
              </button>
              <button
                type="button"
                onClick={handleDownloadPDF}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download PDF
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ItemCreation;
